import React, { useState, useEffect, useRef } from "react";
import {
  IconButton,
  Box,
  Fab,
  LinearProgress,
  Modal,
} from "@mui/material";
import {
  Close as CloseIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  PlayArrow as PlayIcon,
  VolumeOff as MuteIcon,
  VolumeUp as UnmuteIcon,
} from "@mui/icons-material";

const VideoPopup = ({ 
  isOpen, 
  onClose, 
  selectedReel, 
  reels, 
  onPrevious, 
  onNext, 
  dataUrl 
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  const videoRef = useRef(null);

  // Handle play/pause
  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Handle mute toggle
  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  // Update progress bar
  const updateProgress = () => {
    if (videoRef.current) {
      const current = videoRef.current.currentTime;
      const total = videoRef.current.duration;
      setProgress((current / total) * 100);
    }
  };

  // Handle video loaded metadata
  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  // Handle video time update
  const handleTimeUpdate = () => {
    updateProgress();
  };

  // Handle video end
  const handleVideoEnd = () => {
    setIsPlaying(false);
    setProgress(0);
  };

  // Handle progress bar click for seeking
  const handleProgressClick = (event) => {
    if (videoRef.current && duration > 0) {
      const rect = event.currentTarget.getBoundingClientRect();
      const clickX = event.clientX - rect.left;
      const percentage = clickX / rect.width;
      const newTime = percentage * duration;
      
      videoRef.current.currentTime = newTime;
      setProgress(percentage * 100);
    }
  };

  // Reset video state when reel changes
  useEffect(() => {
    if (selectedReel && videoRef.current) {
      setIsPlaying(false);
      setProgress(0);
    }
  }, [selectedReel]);

  // Auto-play when video loads
  useEffect(() => {
    if (selectedReel && videoRef.current && isOpen) {
      videoRef.current.play();
      setIsPlaying(true);
      setIsMuted(false);
    }
  }, [selectedReel, isOpen, videoRef]);

  // Handle body scroll when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
      setIsMuted(false);
      setIsPlaying(true);
    } else {
      // Restore body scroll when modal is closed
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to restore scroll when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      fullScreen
      
    >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '100vh',
            backgroundColor: '#000',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >

          
          {/* Mute Button */}
          <IconButton
            onClick={toggleMute}
            sx={{
              position: 'absolute',
              top: 20,
              right: 80,
              zIndex: 20,
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
              },
            }}
          >
            {isMuted ? <MuteIcon /> : <UnmuteIcon />}
          </IconButton>

          {/* Close Button */}
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 20,
              right: 16,
              zIndex: 20,
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Navigation Buttons */}
          {reels && reels.length > 1 && (
            <>
              <Fab
                onClick={onPrevious}
                sx={{
                  position: 'absolute',
                  left: 20,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 15,
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  color: '#fff',
                  width: 50,
                  height: 50,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    transform: 'translateY(-50%) scale(1.1)',
                  },
                  display: 'flex',
                }}
                size="medium"
              >
                <PrevIcon />
              </Fab>

              <Fab
                onClick={onNext}
                sx={{
                  position: 'absolute',
                  right: 20,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 15,
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  color: '#fff',
                  width: 50,
                  height: 50,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    transform: 'translateY(-50%) scale(1.1)',
                  },
                  display: 'flex',
                }}
                size="medium"
              >
                <NextIcon />
              </Fab>
            </>
          )}


          {/* Video Container */}
          {selectedReel && (
            <Box
              sx={{
                position: 'relative',
                maxWidth: '100%',
                maxHeight: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <video
                ref={videoRef}
                src={`${dataUrl}/contents/${selectedReel.additional_fields?.video}`}
                controls={false}
                autoPlay
                muted={isMuted}
                onLoadedMetadata={handleLoadedMetadata}
                onTimeUpdate={handleTimeUpdate}
                onEnded={handleVideoEnd}
                onClick={togglePlayPause}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  cursor: 'pointer',
                }}
              />

              {/* Transparent Overlay - Only show when paused */}
              {!isPlaying && (
                <Box
                  onClick={togglePlayPause}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    zIndex: 10,
                  }}
                >
                  {/* Custom Play Button - Only show when paused */}
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePlayPause();
                    }}
                    sx={{
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      color: 'white',
                      width: 80,
                      height: 80,
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.9)',
                        transform: 'scale(1.1)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <PlayIcon sx={{ fontSize: 40 }} />
                  </IconButton>
                </Box>
              )}

              {/* Custom Progress Bar - Only at top of video */}
              <Box
                onClick={handleProgressClick}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  zIndex: 20,
                  cursor: 'pointer',
                }}
              >
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: '#fff',
                      boxShadow: '0 0 12px rgba(255, 255, 255, 0.8)',
                    },
                    '&:hover': {
                      height: 10,
                      backgroundColor: 'rgba(255, 255, 255, 0.4)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: '#fff',
                        boxShadow: '0 0 16px rgba(255, 255, 255, 1)',
                      },
                    },
                    transition: 'all 0.3s ease',
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>
    </Modal>
  );
};

export default VideoPopup;
